import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useImportIssuesDownload } from "@/hooks/useImportIssuesDownload";
import { restApi, useGetImportRunQuery } from "@/store/rest_api";
import {
  importDestinationPath,
  importRecordsPath,
  importRecordTags,
  importStatusPills,
  isImportRunActive,
  type ImportRecordAction,
} from "@/util/importRuns";

interface ImportProgressStepProps {
  runId: string;
  onNavigate: () => void;
}

interface ImportStat {
  label: string;
  value: number;
  hint?: string;
  action?: ImportRecordAction;
}

const POLL_INTERVAL_MS = 2000;
const QUEUED_POLL_INTERVAL_MS = 60000;

export default function ImportProgressStep({ runId, onNavigate }: ImportProgressStepProps) {
  const dispatch = useDispatch();
  const [pollingInterval, setPollingInterval] = useState(POLL_INTERVAL_MS);
  const { data: run } = useGetImportRunQuery(runId, { pollingInterval });
  const { downloadIssues, downloadingIssues } = useImportIssuesDownload();

  const finishedStatus = run && !isImportRunActive(run) ? run.status : null;
  const queued = run?.status === "PENDING";

  useEffect(() => {
    if (!finishedStatus) {
      return;
    }
    setPollingInterval(0);
    dispatch(restApi.util.invalidateTags([...importRecordTags]));
  }, [finishedStatus, dispatch]);

  useEffect(() => {
    if (finishedStatus) {
      return;
    }
    setPollingInterval(queued ? QUEUED_POLL_INTERVAL_MS : POLL_INTERVAL_MS);
  }, [queued, finishedStatus]);

  if (!run) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Queueing the import
      </div>
    );
  }

  const active = isImportRunActive(run);
  const percent = run.totalRows > 0 ? Math.min(100, Math.round((run.processedRows / run.totalRows) * 100)) : 0;
  const pill = importStatusPills[run.status];

  if (queued) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 truncate text-sm font-medium">{run.fileName}</div>
          <span className={pill.className}>{pill.label}</span>
        </div>
        <p className="text-sm text-muted-foreground">
          Your import is running, and you get a notification when it is done. You can close this window.
        </p>
      </div>
    );
  }
  const destinationPath = importDestinationPath(run);
  const stats: ImportStat[] = [
    { label: "Added", value: run.addedCount, action: "CREATED" },
    { label: "Updated", value: run.updatedCount, action: "UPDATED" },
    { label: "Duplicates", value: run.duplicateCount, hint: "Rows repeated in the file, combined into one record" },
    { label: "Skipped", value: run.skippedCount, hint: "Rows with nothing to import" },
    { label: "Errors", value: run.errorCount, hint: "Rows that could not be saved" },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 truncate text-sm font-medium">{run.fileName}</div>
        <span className={pill.className}>{pill.label}</span>
      </div>

      <div className="space-y-1.5">
        <Progress value={active ? percent : 100} />
        <div className="text-xs tabular-nums text-muted-foreground">
          {run.processedRows.toLocaleString()} of {run.totalRows.toLocaleString()} rows
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        {stats.map((stat) => {
          const path = stat.action && stat.value > 0 && !active ? importRecordsPath(run, stat.action) : null;
          const content = (
            <>
              <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{stat.label}</div>
              <div className="text-lg font-semibold tabular-nums">{stat.value.toLocaleString()}</div>
            </>
          );
          if (path) {
            return (
              <Link
                key={stat.label}
                to={path}
                onClick={onNavigate}
                className="rounded-[var(--radius)] border border-border p-3 transition-colors hover:border-primary"
              >
                {content}
              </Link>
            );
          }
          return (
            <div key={stat.label} title={stat.hint} className="rounded-[var(--radius)] border border-border p-3">
              {content}
            </div>
          );
        })}
      </div>

      {run.status === "FAILED" && run.errorSummary && <p className="text-sm text-destructive">{run.errorSummary}</p>}

      {active ? (
        <p className="text-sm text-muted-foreground">
          You can close this window. The import keeps running, and you get a notification when it finishes.
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => downloadIssues(run)} disabled={downloadingIssues}>
            {downloadingIssues ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
            Download row issues
          </Button>
          {destinationPath && (
            <Button variant="outline" asChild>
              <Link to={destinationPath} onClick={onNavigate}>
                {run.entityType === "DNC" ? "Open Do Not Contact" : "Open tasks"}
              </Link>
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
